import * as React from 'react';

import { cn } from '@/lib/utils';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';

type ShellVariant = 'marketing' | 'app' | 'utility';
type HeaderAlign = 'left' | 'center';
type HeroVariant = 'surface' | 'plain';
type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
type MobileSectionStyle = 'default' | 'band';
type PhonePanelStyle = 'default' | 'open';

type SiteShellProps = {
  children: React.ReactNode;
  variant?: ShellVariant;
  hero?: React.ReactNode;
  className?: string;
  mainClassName?: string;
  contentClassName?: string;
};

type PageIntroProps = {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  align?: HeaderAlign;
  compact?: boolean;
  headingLevel?: HeadingLevel;
  className?: string;
  children?: React.ReactNode;
};

type PanelTone = 'default' | 'muted' | 'deep';

export function SiteShell({
  children,
  variant = 'marketing',
  hero,
  className,
  mainClassName,
  contentClassName,
}: SiteShellProps) {
  const mainWidthClass = 'site-shell-wide';
  const mainVariantClass = variant === 'marketing' ? 'site-main-marketing' : undefined;

  return (
    <div
      className={cn(
        'site-shell',
        variant === 'marketing' && 'site-shell-marketing',
        variant === 'app' && 'site-shell-app-surface',
        variant === 'utility' && 'site-shell-utility',
        className,
      )}
    >
      <Navbar variant={variant} />
      <main className={cn('site-main', mainVariantClass, mainClassName)}>
        <div className={cn('site-shell-frame', mainWidthClass, contentClassName)}>
          {hero}
          {children}
        </div>
      </main>
      <Footer variant={variant} />
    </div>
  );
}

export function MarketingPageShell(props: Omit<SiteShellProps, 'variant'>) {
  return <SiteShell variant="marketing" {...props} />;
}

export function AppPageShell(props: Omit<SiteShellProps, 'variant'>) {
  return <SiteShell variant="marketing" {...props} />;
}

export function UtilityPageShell(props: Omit<SiteShellProps, 'variant'>) {
  return <SiteShell variant="marketing" {...props} />;
}

export function PageSection({
  children,
  className,
  mobileStyle = 'default',
}: {
  children: React.ReactNode;
  className?: string;
  mobileStyle?: MobileSectionStyle;
}) {
  return (
    <section
      className={cn(
        'site-section',
        mobileStyle === 'band' && 'site-section-mobile-band',
        className,
      )}
    >
      {children}
    </section>
  );
}

export function PageIntro({
  eyebrow,
  title,
  description,
  actions,
  align = 'left',
  compact = false,
  headingLevel = 'h1',
  className,
  children,
}: PageIntroProps) {
  const centered = align === 'center';
  const HeadingTag = headingLevel;

  return (
    <div
      className={cn(
        'site-intro',
        compact ? 'gap-4' : 'gap-6',
        centered && 'items-center text-center',
        className,
      )}
    >
      {eyebrow ? <div className="site-eyebrow">{eyebrow}</div> : null}
      <div className={cn('flex flex-col', compact ? 'gap-3' : 'gap-4', centered && 'items-center')}>
        <HeadingTag className={cn('site-title', compact && 'site-title-compact')}>{title}</HeadingTag>
        {description ? <p className={cn('site-copy', centered && 'mx-auto max-w-3xl')}>{description}</p> : null}
      </div>
      {actions ? (
        <div
          className={cn(
            'flex flex-wrap gap-3',
            centered ? 'justify-center' : 'justify-start',
          )}
        >
          {actions}
        </div>
      ) : null}
      {children}
    </div>
  );
}

export function PageHero({
  children,
  className,
  surfaceClassName,
  variant = 'surface',
}: {
  children: React.ReactNode;
  className?: string;
  surfaceClassName?: string;
  variant?: HeroVariant;
}) {
  if (variant === 'plain') {
    return (
      <section className={cn('site-hero-shell-plain', className)}>
        <div className={cn('site-hero-surface-plain', surfaceClassName)}>{children}</div>
      </section>
    );
  }

  return (
    <section className={cn('site-hero-shell', className)}>
      <div className={cn('site-hero-surface', surfaceClassName)}>{children}</div>
    </section>
  );
}

type SurfacePanelProps = React.HTMLAttributes<HTMLDivElement> & {
  tone?: PanelTone;
  phoneStyle?: PhonePanelStyle;
};

export const SurfacePanel = React.forwardRef<HTMLDivElement, SurfacePanelProps>(function SurfacePanel(
  {
    children,
    className,
    tone = 'default',
    phoneStyle = 'default',
    ...props
  },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        'site-panel',
        tone === 'muted' && 'site-panel-muted',
        tone === 'deep' && 'site-panel-deep',
        phoneStyle === 'open' && 'site-panel-phone-open',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
});

export function SectionPill({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <span className={cn('site-pill', className)}>{children}</span>;
}

export function SectionStack({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn('flex flex-col gap-6', className)}>{children}</div>;
}

export function EmptyState({
  title,
  description,
  action,
  className,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <SurfacePanel className={cn('flex flex-col gap-4 text-left', className)} tone="muted">
      <div className="flex flex-col gap-2">
        <h2 className="text-lg font-semibold text-[var(--site-ink-strong)]">{title}</h2>
        {description ? <p className="text-sm text-[var(--site-ink-muted)]">{description}</p> : null}
      </div>
      {action}
    </SurfacePanel>
  );
}
